import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, ChevronsUpDown, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { usePageParams } from '@/hooks/usePageParams';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type ProjectOption = { id: string; project_code: string; project_name?: string | null };
type SkuOption = {
  id: string;
  sku_code: string;
  name: string;
  category?: string | null;
  uom?: string | null;
  material_grade?: string | null;
  pipe_type?: string | null;
  nominal_size?: string | null;
  schedule?: string | null;
  pipe_length?: string | null;
  thickness_mm?: number | null;
  width_mm?: number | null;
  length_mm?: number | null;
};

type RequestLineMeta = {
  materialType?: string;
  smls?: boolean;
  wld?: boolean;
  nominalSize?: string;
  schedule?: string;
  length?: string;
  pipeCode?: string;
  stockNo?: string;
  heatNo?: string;
  millCert?: boolean;
  dimension?: string;
  grade?: string;
  location?: string;
  checkedRef?: string;
  receivedByContractorDate?: string;
  returnQty?: string;
  irtNo?: string;
  lineDate?: string;
  remark?: string;
};

type RequestLine = {
  id: string;
  sku_id?: string | null;
  line_number: number;
  sku_code: string | null;
  sku_name: string | null;
  qty_ordered: number;
  qty_fulfilled: number;
  uom: string | null;
  lot_number: string | null;
  batch_code: string | null;
  notes: string | null;
  metadata?: Record<string, unknown> | null;
};

type RequestDetail = {
  id: string;
  project_id: string | null;
  project_code: string | null;
  counterparty_name: string | null;
  order_number: string;
  order_type: string;
  department: string | null;
  requester_name: string | null;
  required_date: string | null;
  order_date?: string | null;
  priority: string | null;
  status: string;
  notes: string | null;
  metadata?: Record<string, unknown> | null;
  total_qty: number;
  total_fulfilled: number;
  lines: RequestLine[];
};

type RequestForm = 'PIPE' | 'PLATE' | 'GENERAL';

function normalizeRequestFormParam(value: string | null | undefined): RequestForm {
  const normalized = String(value ?? '').toUpperCase();
  if (normalized === 'PLATE') return 'PLATE';
  if (normalized === 'GENERAL') return 'GENERAL';
  return 'PIPE';
}

type HeaderDraft = {
  requestForm: RequestForm;
  projectId: string;
  requestDate: string;
  requestedBy: string;
  authorisedBy: string;
  approvedBy: string;
  issuedBy: string;
  updatedBy: string;
  contractor: string;
  jobNo: string;
  approvalRequired: string;
  notes: string;
};

type LineDraft = {
  id: string;
  isNew: boolean;
  skuId: string;
  skuCode: string;
  skuName: string;
  qtyReq: string;
  uom: string;
  smls: boolean;
  wld: boolean;
  nominalSize: string;
  schedule: string;
  length: string;
  pipeCode: string;
  stockNo: string;
  heatNo: string;
  millCert: boolean;
  dimension: string;
  grade: string;
  location: string;
  checkedRef: string;
  receivedByContractorDate: string;
  returnQty: string;
  irtNo: string;
  lineDate: string;
  remark: string;
};

function requestByLabel(requestForm: RequestForm) {
  if (requestForm === 'PIPE') return 'Request By';
  if (requestForm === 'PLATE') return 'Initiated By';
  return 'Requested By';
}

function issuedByLabel(requestForm: RequestForm) {
  return requestForm === 'PIPE' ? 'Issue By' : 'Issued By';
}

const REQUEST_FORM_OPTIONS: Array<{ value: RequestForm; label: string }> = [
  { value: 'PIPE', label: 'Pipe Request Slip' },
  { value: 'PLATE', label: 'Plate Request Form' },
  { value: 'GENERAL', label: 'General Material Request' },
];

const LINE_UOM_OPTIONS = ['EA', 'PCS', 'BOX', 'CARTON', 'PALLET', 'KG', 'M', 'CM', 'MM', 'CBM'];

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
}

function parseBooleanFlag(value: unknown) {
  if (value === true) return true;
  const normalized = String(value ?? '').trim().toUpperCase();
  return normalized === 'YES' || normalized === 'Y' || normalized === 'TRUE' || normalized === '1';
}

function normalizeYesNo(value: unknown, fallback = 'NO') {
  const normalized = String(value ?? '').trim().toUpperCase();
  if (normalized === 'YES' || normalized === 'Y' || normalized === 'TRUE' || normalized === '1') return 'YES';
  if (normalized === 'NO' || normalized === 'N' || normalized === 'FALSE' || normalized === '0') return 'NO';
  return fallback;
}

function asMeta(value: Record<string, unknown> | null | undefined): RequestLineMeta {
  return (value ?? {}) as RequestLineMeta;
}

function inferRequestForm(order: RequestDetail): RequestForm {
  const explicit = String(order.metadata?.requestForm ?? '').toUpperCase();
  if (explicit === 'PIPE' || explicit === 'PLATE' || explicit === 'GENERAL') return explicit as RequestForm;
  const haystack = (order.lines ?? [])
    .map((line) => `${line.sku_code ?? ''} ${line.sku_name ?? ''} ${line.batch_code ?? ''}`.toUpperCase())
    .join(' ');
  if (haystack.includes('PLATE')) return 'PLATE';
  if (haystack.includes('PIPE')) return 'PIPE';
  return 'GENERAL';
}

function buildPlateDimension(sku: SkuOption) {
  const parts = [sku.thickness_mm, sku.width_mm, sku.length_mm]
    .filter((value) => value !== null && value !== undefined)
    .map((value) => String(value));
  return parts.length ? parts.join(' x ') : '';
}

function buildSkuPatch(sku: SkuOption, requestForm: RequestForm, currentLine: LineDraft): Partial<LineDraft> {
  const pipeType = String(sku.pipe_type ?? '').toUpperCase();
  const isSmls = pipeType.includes('SMLS');
  const isWld = pipeType.includes('WLD') || pipeType.includes('WELD');

  if (requestForm === 'PIPE') {
    return {
      skuId: sku.id,
      skuCode: sku.sku_code,
      skuName: sku.name,
      uom: 'M',
      smls: isSmls,
      wld: isWld,
      nominalSize: sku.nominal_size ?? currentLine.nominalSize,
      schedule: sku.schedule ?? currentLine.schedule,
      length: sku.pipe_length ?? (sku.length_mm ? String(sku.length_mm) : currentLine.length),
      grade: sku.material_grade ?? currentLine.grade,
    };
  }

  if (requestForm === 'PLATE') {
    return {
      skuId: sku.id,
      skuCode: sku.sku_code,
      skuName: sku.name,
      uom: sku.uom ?? currentLine.uom ?? 'PCS',
      dimension: buildPlateDimension(sku) || currentLine.dimension,
      grade: sku.material_grade ?? currentLine.grade,
    };
  }

  return {
    skuId: sku.id,
    skuCode: sku.sku_code,
    skuName: sku.name,
    uom: sku.uom ?? currentLine.uom ?? 'PCS',
  };
}

function buildHeaderDraft(order: RequestDetail): HeaderDraft {
  const resolvedProjectCode = order.project_code ?? order.department ?? '';
  return {
    requestForm: inferRequestForm(order),
    projectId: order.project_id ?? '',
    requestDate: formatDate(order.required_date ?? order.order_date),
    requestedBy: order.requester_name ?? '',
    authorisedBy: String(order.metadata?.authorisedBy ?? order.metadata?.approvedBy ?? ''),
    approvedBy: String(order.metadata?.approvedBy ?? ''),
    issuedBy: String(order.metadata?.issuedBy ?? ''),
    updatedBy: String(order.metadata?.updatedBy ?? ''),
    contractor: order.counterparty_name ?? '',
    jobNo: resolvedProjectCode,
    approvalRequired: String(order.metadata?.approvalRequired ?? order.metadata?.requestApprovalRequired ?? true) === 'false' ? 'false' : 'true',
    notes: order.notes ?? '',
  };
}

function buildLineDraft(line: RequestLine, requestForm: RequestForm): LineDraft {
  const meta = asMeta(line.metadata);
  return {
    id: line.id,
    isNew: false,
    skuId: String(line.sku_id ?? ''),
    skuCode: line.sku_code ?? '',
    skuName: line.sku_name ?? '',
    qtyReq: String(line.qty_ordered ?? 0),
    uom: line.uom ?? 'PCS',
    smls: Boolean(meta.smls),
    wld: Boolean(meta.wld),
    nominalSize: String(meta.nominalSize ?? ''),
    schedule: String(meta.schedule ?? ''),
    length: String(meta.length ?? ''),
    pipeCode: String(meta.pipeCode ?? (requestForm === 'PIPE' ? line.batch_code ?? '' : '')),
    stockNo: String(meta.stockNo ?? (requestForm === 'PLATE' ? line.batch_code ?? '' : '')),
    heatNo: String(meta.heatNo ?? line.lot_number ?? ''),
    millCert: parseBooleanFlag(meta.millCert),
    dimension: String(meta.dimension ?? ''),
    grade: String(meta.grade ?? ''),
    location: String(meta.location ?? ''),
    checkedRef: normalizeYesNo(meta.checkedRef),
    receivedByContractorDate: String(meta.receivedByContractorDate ?? ''),
    returnQty: String(meta.returnQty ?? ''),
    irtNo: String(meta.irtNo ?? ''),
    lineDate: formatDate(String(meta.lineDate ?? '')),
    remark: String(meta.remark ?? line.notes ?? ''),
  };
}

function newLineDraft(): LineDraft {
  return {
    id: `new-${Math.random().toString(36).slice(2, 8)}`,
    isNew: true,
    skuId: '',
    skuCode: '',
    skuName: '',
    qtyReq: '1',
    uom: 'PCS',
    smls: false,
    wld: false,
    nominalSize: '',
    schedule: '',
    length: '0',
    pipeCode: '',
    stockNo: '',
    heatNo: '',
    millCert: false,
    dimension: '',
    grade: '',
    location: '',
    checkedRef: 'NO',
    receivedByContractorDate: '',
    returnQty: '0',
    irtNo: '',
    lineDate: '',
    remark: '',
  };
}

function statusTone(status: string | null | undefined) {
  switch (status) {
    case 'FULFILLED':
    case 'CLOSED':
      return 'bg-green-100 text-green-700';
    case 'PROCESSING':
    case 'PARTIALLY_FULFILLED':
      return 'bg-blue-100 text-blue-700';
    case 'CANCELLED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function stepLabel(step: 'header' | 'items') {
  return step === 'header' ? 'Step 1 of 2' : 'Step 2 of 2';
}

function linePayload(line: LineDraft, requestForm: RequestForm) {
  const heatNo = requestForm === 'PLATE' ? line.heatNo : '';
  return {
    skuId: line.skuId || undefined,
    skuCode: line.skuCode || undefined,
    skuName: line.skuName || undefined,
    qtyOrdered: Number(line.qtyReq || 0),
    uom: line.uom || 'PCS',
    lotNumber: heatNo || undefined,
    batchCode: requestForm === 'PLATE' ? (line.stockNo || undefined) : (line.pipeCode || undefined),
    notes: line.remark || undefined,
    metadata: {
      requestForm,
      materialType: requestForm === 'GENERAL' ? 'OTHER' : requestForm,
      smls: line.smls,
      wld: line.wld,
      nominalSize: line.nominalSize || '',
      schedule: line.schedule || '',
      length: line.length || '',
      pipeCode: line.pipeCode || '',
      stockNo: line.stockNo || '',
      heatNo,
      millCert: line.millCert,
      dimension: line.dimension || '',
      grade: line.grade || '',
      location: line.location || '',
      checkedRef: line.checkedRef || 'NO',
      receivedByContractorDate: line.receivedByContractorDate || '',
      returnQty: line.returnQty || '0',
      irtNo: line.irtNo || '',
      lineDate: line.lineDate || '',
      remark: line.remark || '',
    },
  };
}

function MobileSkuPicker({
  warehouseId,
  requestForm,
  value,
  onSelect,
}: {
  warehouseId?: string;
  requestForm: RequestForm;
  value: string;
  onSelect: (sku: SkuOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<SkuOption[]>([]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ status: 'ACTIVE', limit: '40' });
    if (warehouseId) params.set('warehouseId', warehouseId);
    if (requestForm !== 'GENERAL') params.set('category', requestForm);
    if (query.trim()) params.set('search', query.trim());
    void api.get<SkuOption[]>(`/wms/skus?${params.toString()}`)
      .then((rows) => {
        if (!cancelled) setOptions(rows);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, query, requestForm, warehouseId]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between">
          <span className="truncate text-left">{value || 'Search SKU by code or name'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Search SKU code or name..." />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading SKU list...
              </div>
            ) : null}
            {!loading && <CommandEmpty>No matching SKU found.</CommandEmpty>}
            <CommandGroup>
              {options.map((sku) => (
                <CommandItem
                  key={sku.id}
                  value={`${sku.sku_code} ${sku.name}`}
                  onSelect={() => {
                    onSelect(sku);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === sku.sku_code ? 'opacity-100' : 'opacity-0')} />
                  <div className="min-w-0">
                    <div className="truncate font-medium">{sku.sku_code}</div>
                    <div className="truncate text-xs text-muted-foreground">{sku.name}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function DesktopMaterialLinesTable({
  lines,
  requestForm,
  warehouseId,
  savingLineId,
  onPatchLine,
  onSaveLine,
  onDeleteLine,
}: {
  lines: LineDraft[];
  requestForm: RequestForm;
  warehouseId?: string;
  savingLineId: string | null;
  onPatchLine: (lineId: string, patch: Partial<LineDraft>) => void;
  onSaveLine: (line: LineDraft) => void;
  onDeleteLine: (line: LineDraft) => void;
}) {
  const isPipe = requestForm === 'PIPE';
  const isPlate = requestForm === 'PLATE';

  return (
    <div className="hidden overflow-x-auto rounded-lg border bg-background md:block">
      <table className="w-full min-w-[1260px] text-sm">
        <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="w-14 px-3 py-2 text-left">Line</th>
            <th className="min-w-[320px] px-3 py-2 text-left">Material</th>
            <th className="w-32 px-3 py-2 text-left">Request Qty</th>
            <th className="w-32 px-3 py-2 text-left">Request Length</th>
            <th className="w-32 px-3 py-2 text-left">UOM</th>
            {isPipe ? <th className="min-w-[280px] px-3 py-2 text-left">Pipe Detail</th> : null}
            {isPlate ? <th className="min-w-[300px] px-3 py-2 text-left">Plate Detail</th> : null}
            {isPlate ? <th className="min-w-[180px] px-3 py-2 text-left">Heat / Cert</th> : null}
            <th className="min-w-[170px] px-3 py-2 text-left">Location</th>
            {isPlate ? <th className="min-w-[230px] px-3 py-2 text-left">Return / IRT</th> : null}
            <th className="min-w-[220px] px-3 py-2 text-left">Remark</th>
            <th className="w-32 px-3 py-2 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={line.id} className="border-t align-top">
              <td className="px-3 py-3 font-medium">{index + 1}</td>
              <td className="space-y-2 px-3 py-3">
                <MobileSkuPicker
                  warehouseId={warehouseId}
                  requestForm={requestForm}
                  value={line.skuCode ? `${line.skuCode} ${line.skuName}` : ''}
                  onSelect={(sku) => onPatchLine(line.id, buildSkuPatch(sku, requestForm, line))}
                />
                <Input
                  value={line.skuName}
                  onChange={(event) => onPatchLine(line.id, { skuName: event.target.value })}
                  placeholder="Material description / spec"
                />
              </td>
              <td className="px-3 py-3">
                <Input
                  type="number"
                  min="0"
                  value={line.qtyReq}
                  onChange={(event) => onPatchLine(line.id, { qtyReq: event.target.value })}
                  placeholder="Qty"
                />
              </td>
              <td className="px-3 py-3">
                <Input
                  type="number"
                  min="0"
                  value={line.length}
                  onChange={(event) => onPatchLine(line.id, { length: event.target.value })}
                  placeholder="Length"
                />
              </td>
              <td className="px-3 py-3">
                <Select value={line.uom} onValueChange={(value) => onPatchLine(line.id, { uom: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LINE_UOM_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </td>
              {isPipe ? (
                <td className="space-y-2 px-3 py-3">
                  <div className="grid grid-cols-2 gap-2">
                    <label className="flex items-center gap-2 rounded-md border px-2 py-2">
                      <Checkbox checked={line.smls} onCheckedChange={(checked) => onPatchLine(line.id, { smls: checked === true })} />
                      SMLS
                    </label>
                    <label className="flex items-center gap-2 rounded-md border px-2 py-2">
                      <Checkbox checked={line.wld} onCheckedChange={(checked) => onPatchLine(line.id, { wld: checked === true })} />
                      WLD
                    </label>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input value={line.nominalSize} onChange={(event) => onPatchLine(line.id, { nominalSize: event.target.value })} placeholder="Nominal" />
                    <Input value={line.schedule} onChange={(event) => onPatchLine(line.id, { schedule: event.target.value })} placeholder="Sch" />
                    <Input value={line.length} readOnly className="bg-muted/40" placeholder="Length" />
                  </div>
                  <Input value={line.pipeCode} onChange={(event) => onPatchLine(line.id, { pipeCode: event.target.value })} placeholder="Pipe code / stock ref" />
                </td>
              ) : null}
              {isPlate ? (
                <td className="space-y-2 px-3 py-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={line.stockNo} onChange={(event) => onPatchLine(line.id, { stockNo: event.target.value })} placeholder="Stock No" />
                    <Input value={line.grade} onChange={(event) => onPatchLine(line.id, { grade: event.target.value })} placeholder="Grade" />
                  </div>
                  <Input value={line.dimension} onChange={(event) => onPatchLine(line.id, { dimension: event.target.value })} placeholder="Dimension" />
                  <Select value={line.checkedRef} onValueChange={(value) => onPatchLine(line.id, { checkedRef: value })}>
                    <SelectTrigger><SelectValue placeholder="Checked Plate / SRF / Heat No" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="YES">Checked</SelectItem>
                      <SelectItem value="NO">Not checked</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
              ) : null}
              {isPlate ? (
                <td className="space-y-2 px-3 py-3">
                  <Input value={line.heatNo} onChange={(event) => onPatchLine(line.id, { heatNo: event.target.value })} placeholder="Heat No" />
                  <label className="flex items-center gap-2 rounded-md border px-2 py-2">
                    <Checkbox checked={line.millCert} onCheckedChange={(checked) => onPatchLine(line.id, { millCert: checked === true })} />
                    Mill cert
                  </label>
                </td>
              ) : null}
              <td className="px-3 py-3">
                <Input value={line.location} onChange={(event) => onPatchLine(line.id, { location: event.target.value })} placeholder="Bin / rack / yard" />
              </td>
              {isPlate ? (
                <td className="space-y-2 px-3 py-3">
                  <Input value={line.receivedByContractorDate} onChange={(event) => onPatchLine(line.id, { receivedByContractorDate: event.target.value })} placeholder="Received by / date" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" min="0" value={line.returnQty} onChange={(event) => onPatchLine(line.id, { returnQty: event.target.value })} placeholder="Return Qty" />
                    <Input value={line.irtNo} onChange={(event) => onPatchLine(line.id, { irtNo: event.target.value })} placeholder="IRT No" />
                  </div>
                  <Input type="date" value={line.lineDate} onChange={(event) => onPatchLine(line.id, { lineDate: event.target.value })} />
                </td>
              ) : null}
              <td className="px-3 py-3">
                <Textarea rows={4} value={line.remark} onChange={(event) => onPatchLine(line.id, { remark: event.target.value })} placeholder="Line remark" />
              </td>
              <td className="px-3 py-3">
                <div className="flex flex-col items-stretch gap-2">
                  <Button type="button" size="sm" onClick={() => onSaveLine(line)} disabled={savingLineId === line.id}>
                    {savingLineId === line.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => onDeleteLine(line)} disabled={savingLineId === line.id}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function WmsMaterialRequestMobileFlow() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id } = usePageParams<{ id?: string }>();
  const requestId = id;
  const requestedForm = normalizeRequestFormParam(searchParams.get('requestForm'));
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [loadingMaster, setLoadingMaster] = useState(true);
  const [loading, setLoading] = useState(Boolean(requestId));
  const [savingHeader, setSavingHeader] = useState(false);
  const [savingLineId, setSavingLineId] = useState<string | null>(null);
  const [order, setOrder] = useState<RequestDetail | null>(null);
  const [headerDraft, setHeaderDraft] = useState<HeaderDraft>({
    requestForm: requestedForm,
    projectId: '',
    requestDate: todayIsoDate(),
    requestedBy: '',
    authorisedBy: '',
    approvedBy: '',
    issuedBy: '',
    updatedBy: '',
    contractor: '',
    jobNo: '',
    approvalRequired: 'true',
    notes: '',
  });
  const [lineDrafts, setLineDrafts] = useState<LineDraft[]>([]);
  const [currentStep, setCurrentStep] = useState<'header' | 'items'>(
    searchParams.get('step') === 'items' ? 'items' : 'header',
  );

  const step = currentStep;
  const requestForm = headerDraft.requestForm;
  const projectLabel = useMemo(
    () => projects.find((project) => project.id === headerDraft.projectId)?.project_code ?? order?.project_code ?? '-',
    [headerDraft.projectId, order?.project_code, projects],
  );

  const setStep = useCallback((nextStep: 'header' | 'items') => {
    setCurrentStep(nextStep);
    const next = new URLSearchParams(searchParams);
    next.set('step', nextStep);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const routeStep = searchParams.get('step');
    if (routeStep === 'items' || routeStep === 'header') {
      setCurrentStep(routeStep);
    }
  }, [searchParams]);

  useEffect(() => {
    if (requestId) return;
    setHeaderDraft((prev) => (
      prev.requestForm === requestedForm
        ? prev
        : { ...prev, requestForm: requestedForm }
    ));
  }, [requestId, requestedForm]);

  useEffect(() => {
    if (requestId) return;
    if (searchParams.get('requestForm') === headerDraft.requestForm) return;
    const next = new URLSearchParams(searchParams);
    next.set('requestForm', headerDraft.requestForm);
    setSearchParams(next, { replace: true });
  }, [headerDraft.requestForm, requestId, searchParams, setSearchParams]);

  const fetchOrder = useCallback(async () => {
    if (!requestId) {
      setOrder(null);
      setLineDrafts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const detail = await api.get<RequestDetail>(`/wms/business-orders/${requestId}`);
      const nextHeader = buildHeaderDraft(detail);
      setOrder(detail);
      setHeaderDraft(nextHeader);
      setLineDrafts((detail.lines ?? []).map((line) => buildLineDraft(line, nextHeader.requestForm)));
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to load material request');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    let cancelled = false;
    setLoadingMaster(true);
    void api.get<ProjectOption[]>('/wms/projects')
      .then((rows) => {
        if (cancelled) return;
        setProjects(rows);
      })
      .catch((err: any) => {
        if (!cancelled) toast.error(err?.message ?? 'Failed to load projects');
      })
      .finally(() => {
        if (!cancelled) setLoadingMaster(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    void fetchOrder();
  }, [fetchOrder]);

  useEffect(() => {
    if (requestId && !searchParams.get('step')) {
      setStep('items');
    }
  }, [requestId, searchParams, setStep]);

  const patchLine = (lineId: string, patch: Partial<LineDraft>) => {
    setLineDrafts((prev) => prev.map((line) => (line.id === lineId ? { ...line, ...patch } : line)));
  };

  const saveHeader = async () => {
    if (!headerDraft.projectId) {
      toast.error('Project is required');
      return;
    }
    if (!headerDraft.requestDate) {
      toast.error('Request date is required');
      return;
    }

    setSavingHeader(true);
    try {
      const resolvedProjectCode = projects.find((project) => project.id === headerDraft.projectId)?.project_code ?? headerDraft.jobNo;
      if (!requestId) {
        const created = await api.post<RequestDetail>('/wms/business-orders', {
          projectId: headerDraft.projectId,
          orderType: 'MATERIAL_REQUEST',
          direction: 'INTERNAL',
          counterpartyName: headerDraft.contractor || undefined,
          department: resolvedProjectCode || undefined,
          requesterName: headerDraft.requestedBy || undefined,
          orderDate: headerDraft.requestDate || undefined,
          requiredDate: headerDraft.requestDate || undefined,
          priority: 'NORMAL',
          notes: headerDraft.notes || undefined,
          metadata: {
            requestForm: headerDraft.requestForm,
            approvalRequired: headerDraft.approvalRequired !== 'false',
            requestApprovalRequired: headerDraft.approvalRequired !== 'false',
            authorisedBy: headerDraft.authorisedBy || '',
            approvedBy: headerDraft.requestForm === 'PIPE' ? (headerDraft.authorisedBy || '') : (headerDraft.approvedBy || ''),
            issuedBy: headerDraft.issuedBy || '',
            updatedBy: headerDraft.updatedBy || '',
          },
        });
        toast.success('Material request created');
        navigate(`/wms/material-requests/${created.id}?step=items`);
        return;
      }

      await api.patch(`/wms/business-orders/${requestId}`, {
        projectId: headerDraft.projectId,
        counterpartyName: headerDraft.contractor || undefined,
        department: resolvedProjectCode || undefined,
        requesterName: headerDraft.requestedBy || undefined,
        orderDate: headerDraft.requestDate || undefined,
        requiredDate: headerDraft.requestDate || undefined,
        notes: headerDraft.notes || undefined,
        metadata: {
          ...(order?.metadata ?? {}),
          requestForm: headerDraft.requestForm,
          approvalRequired: headerDraft.approvalRequired !== 'false',
          requestApprovalRequired: headerDraft.approvalRequired !== 'false',
          authorisedBy: headerDraft.authorisedBy || '',
          approvedBy: headerDraft.requestForm === 'PIPE' ? (headerDraft.authorisedBy || '') : (headerDraft.approvedBy || ''),
          issuedBy: headerDraft.issuedBy || '',
          updatedBy: headerDraft.updatedBy || '',
        },
      });
      toast.success('Request header updated');
      await fetchOrder();
      setStep('items');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to save request header');
    } finally {
      setSavingHeader(false);
    }
  };

  const saveLine = async (line: LineDraft) => {
    if (!requestId) {
      toast.error('Create request header first');
      return;
    }
    if (!line.skuId && !line.skuCode.trim()) {
      toast.error('Please pick a SKU from the list');
      return;
    }
    if (Number(line.qtyReq || 0) <= 0) {
      toast.error('Qty requested must be more than 0');
      return;
    }

    setSavingLineId(line.id);
    try {
      const payload = linePayload(line, requestForm);
      if (line.isNew) {
        await api.post(`/wms/business-orders/${requestId}/lines`, { lines: [payload] });
        toast.success('Item added');
      } else {
        await api.patch(`/wms/business-order-lines/${line.id}`, payload);
        toast.success('Item updated');
      }
      await fetchOrder();
      if (currentStep !== 'items') {
        setStep('items');
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to save request item');
    } finally {
      setSavingLineId(null);
    }
  };

  const deleteLine = async (line: LineDraft) => {
    if (line.isNew) {
      setLineDrafts((prev) => prev.filter((item) => item.id !== line.id));
      return;
    }
    setSavingLineId(line.id);
    try {
      await api.delete(`/wms/business-order-lines/${line.id}`);
      toast.success('Item removed');
      await fetchOrder();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to remove request item');
    } finally {
      setSavingLineId(null);
    }
  };

  if (loadingMaster || loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-3">
      <div className="space-y-2">
        <Button type="button" variant="ghost" size="sm" className="-ml-2" onClick={() => navigate('/wms/material-requests')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Material Requests
        </Button>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{stepLabel(step)}</div>
            <h1 className="text-xl font-semibold tracking-tight">
              {requestId ? (order?.order_number ?? 'Material Request') : 'New Material Request'}
            </h1>
          </div>
          {requestId && order ? (
            <Badge variant="outline" className={statusTone(order.status)}>
              {order.status}
            </Badge>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant={step === 'header' ? 'default' : 'outline'} onClick={() => setStep('header')}>
            1. Header
          </Button>
          <Button type="button" variant={step === 'items' ? 'default' : 'outline'} onClick={() => requestId ? setStep('items') : toast.error('Create header first')}>
            2. Add Items
          </Button>
        </div>
      </div>

      {step === 'header' ? (
        <Card>
          <CardHeader>
            <CardTitle>Request Header</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {requestId ? (
              <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Request No</div>
                <div className="mt-1 font-semibold">{order?.order_number}</div>
              </div>
            ) : (
              <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
                Request no will be auto-generated when you create the header.
              </div>
            )}

            <div>
              <Label>Request Form</Label>
              <Select value={headerDraft.requestForm} onValueChange={(value) => setHeaderDraft((prev) => ({ ...prev, requestForm: value as RequestForm }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REQUEST_FORM_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Project</Label>
              <Select
                value={headerDraft.projectId}
                onValueChange={(value) => {
                  const selectedProject = projects.find((project) => project.id === value) ?? null;
                  setHeaderDraft((prev) => ({
                    ...prev,
                    projectId: value,
                    jobNo: selectedProject?.project_code ?? prev.jobNo,
                  }));
                }}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.project_code}{project.project_name ? ` · ${project.project_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Request Date</Label>
              <Input className="mt-1" type="date" value={headerDraft.requestDate} onChange={(event) => setHeaderDraft((prev) => ({ ...prev, requestDate: event.target.value }))} />
            </div>

            <div>
              <Label>Project / Job No</Label>
              <Input className="mt-1" value={headerDraft.jobNo} disabled />
            </div>

            <div>
              <Label>Contractor</Label>
              <Input className="mt-1" value={headerDraft.contractor} onChange={(event) => setHeaderDraft((prev) => ({ ...prev, contractor: event.target.value }))} />
            </div>

            <div>
              <Label>{requestByLabel(headerDraft.requestForm)}</Label>
              <Input className="mt-1" value={headerDraft.requestedBy} onChange={(event) => setHeaderDraft((prev) => ({ ...prev, requestedBy: event.target.value }))} />
            </div>

            {headerDraft.requestForm === 'PIPE' ? (
              <div>
                <Label>Authorised By</Label>
                <Input className="mt-1" value={headerDraft.authorisedBy} onChange={(event) => setHeaderDraft((prev) => ({ ...prev, authorisedBy: event.target.value }))} />
              </div>
            ) : null}

            {headerDraft.requestForm !== 'PIPE' ? (
              <div>
                <Label>Approved By</Label>
                <Input className="mt-1" value={headerDraft.approvedBy} onChange={(event) => setHeaderDraft((prev) => ({ ...prev, approvedBy: event.target.value }))} />
              </div>
            ) : null}

            <div>
              <Label>{issuedByLabel(headerDraft.requestForm)}</Label>
              <Input className="mt-1" value={headerDraft.issuedBy} onChange={(event) => setHeaderDraft((prev) => ({ ...prev, issuedBy: event.target.value }))} />
            </div>

            <div>
              <Label>Approval</Label>
              <Select value={headerDraft.approvalRequired} onValueChange={(value) => setHeaderDraft((prev) => ({ ...prev, approvalRequired: value }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Required</SelectItem>
                  <SelectItem value="false">Off</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {headerDraft.requestForm !== 'PIPE' ? (
              <div>
                <Label>Updated By</Label>
                <Input className="mt-1" value={headerDraft.updatedBy} onChange={(event) => setHeaderDraft((prev) => ({ ...prev, updatedBy: event.target.value }))} />
              </div>
            ) : null}

            <div>
              <Label>Request Note</Label>
              <Textarea className="mt-1" rows={3} value={headerDraft.notes} onChange={(event) => setHeaderDraft((prev) => ({ ...prev, notes: event.target.value }))} />
            </div>

            <Button type="button" className="w-full" onClick={() => void saveHeader()} disabled={savingHeader}>
              {savingHeader ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {requestId ? 'Save Header' : 'Create Header & Continue'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
            <Card>
              <CardContent className="grid grid-cols-2 gap-3 p-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Request Form</div>
                  <div className="mt-1 font-medium">{REQUEST_FORM_OPTIONS.find((option) => option.value === requestForm)?.label ?? requestForm}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Project</div>
                  <div className="mt-1 font-medium">{projectLabel}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{requestByLabel(requestForm)}</div>
                  <div className="mt-1 font-medium">{headerDraft.requestedBy || '-'}</div>
                </div>
                {requestForm === 'PIPE' ? (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Authorised By</div>
                    <div className="mt-1 font-medium">{headerDraft.authorisedBy || '-'}</div>
                  </div>
                ) : null}
                {requestForm !== 'PIPE' ? (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Approved By</div>
                    <div className="mt-1 font-medium">{headerDraft.approvedBy || '-'}</div>
                  </div>
                ) : null}
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{issuedByLabel(requestForm)}</div>
                  <div className="mt-1 font-medium">{headerDraft.issuedBy || '-'}</div>
                </div>
                {requestForm !== 'PIPE' ? (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Updated By</div>
                    <div className="mt-1 font-medium">{headerDraft.updatedBy || '-'}</div>
                  </div>
                ) : null}
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Project / Job No</div>
                  <div className="mt-1 font-medium">{headerDraft.jobNo || '-'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Contractor</div>
                  <div className="mt-1 font-medium">{headerDraft.contractor || '-'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Request Date</div>
                  <div className="mt-1 font-medium">{headerDraft.requestDate || '-'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Items</div>
                  <div className="mt-1 font-medium">{lineDrafts.length}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Request Note</div>
                  <div className="mt-1 font-medium">{headerDraft.notes || '-'}</div>
                </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" onClick={() => setStep('header')}>Edit Header</Button>
            <Button type="button" onClick={() => setLineDrafts((prev) => [...prev, newLineDraft()])}>
              <Plus className="mr-2 h-4 w-4" />
              Add Item
            </Button>
          </div>

          {lineDrafts.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No items yet. Add items one by one from the SKU list.
              </CardContent>
            </Card>
          ) : null}

          {lineDrafts.length > 0 ? (
            <DesktopMaterialLinesTable
              lines={lineDrafts}
              requestForm={requestForm}
              warehouseId={order?.metadata?.warehouseId ? String(order.metadata.warehouseId) : undefined}
              savingLineId={savingLineId}
              onPatchLine={patchLine}
              onSaveLine={(line) => void saveLine(line)}
              onDeleteLine={(line) => void deleteLine(line)}
            />
          ) : null}

          <div className="space-y-3 md:hidden">
          {lineDrafts.map((line, index) => (
            <Card key={line.id}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-base">Item {line.isNew ? `New ${index + 1}` : index + 1}</CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{requestForm}</Badge>
                    {!line.isNew ? <Badge variant="secondary">Saved</Badge> : null}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>SKU</Label>
                  <div className="mt-1">
                    <MobileSkuPicker
                      warehouseId={order?.metadata?.warehouseId ? String(order.metadata.warehouseId) : undefined}
                      requestForm={requestForm}
                      value={line.skuCode ? `${line.skuCode} ${line.skuName}` : ''}
                      onSelect={(sku) => patchLine(line.id, buildSkuPatch(sku, requestForm, line))}
                    />
                  </div>
                </div>

                <div>
                  <Label>Description</Label>
                  <Input className="mt-1" value={line.skuName} readOnly />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Qty Requested</Label>
                    <Input className="mt-1" type="number" min="0" value={line.qtyReq} onChange={(event) => patchLine(line.id, { qtyReq: event.target.value })} />
                  </div>
                  <div>
                    <Label>UOM</Label>
                    <Select value={line.uom} onValueChange={(value) => patchLine(line.id, { uom: value })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LINE_UOM_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {requestForm === 'PIPE' ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center gap-2 rounded-lg border p-3 text-sm">
                        <Checkbox checked={line.smls} onCheckedChange={(checked) => patchLine(line.id, { smls: checked === true })} />
                        SMLS
                      </label>
                      <label className="flex items-center gap-2 rounded-lg border p-3 text-sm">
                        <Checkbox checked={line.wld} onCheckedChange={(checked) => patchLine(line.id, { wld: checked === true })} />
                        WLD
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Nominal Size</Label>
                        <Input className="mt-1" value={line.nominalSize} onChange={(event) => patchLine(line.id, { nominalSize: event.target.value })} />
                      </div>
                      <div>
                        <Label>Schedule</Label>
                        <Input className="mt-1" value={line.schedule} onChange={(event) => patchLine(line.id, { schedule: event.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Length</Label>
                        <Input className="mt-1" value={line.length} onChange={(event) => patchLine(line.id, { length: event.target.value })} />
                      </div>
                      <div>
                        <Label>Pipe Code</Label>
                        <Input className="mt-1" value={line.pipeCode} onChange={(event) => patchLine(line.id, { pipeCode: event.target.value })} />
                      </div>
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Input className="mt-1" value={line.location} onChange={(event) => patchLine(line.id, { location: event.target.value })} />
                    </div>
                  </>
                ) : null}

                {requestForm === 'PLATE' ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Stock No</Label>
                        <Input className="mt-1" value={line.stockNo} onChange={(event) => patchLine(line.id, { stockNo: event.target.value })} />
                      </div>
                      <div>
                        <Label>Heat No</Label>
                        <Input className="mt-1" value={line.heatNo} onChange={(event) => patchLine(line.id, { heatNo: event.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Dimension</Label>
                        <Input className="mt-1" value={line.dimension} onChange={(event) => patchLine(line.id, { dimension: event.target.value })} />
                      </div>
                      <div>
                        <Label>Grade</Label>
                        <Input className="mt-1" value={line.grade} onChange={(event) => patchLine(line.id, { grade: event.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Location</Label>
                        <Input className="mt-1" value={line.location} onChange={(event) => patchLine(line.id, { location: event.target.value })} />
                      </div>
                      <div>
                        <Label>Checked Plate / SRF / Heat No</Label>
                        <Select value={line.checkedRef} onValueChange={(value) => patchLine(line.id, { checkedRef: value })}>
                          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="YES">YES</SelectItem>
                            <SelectItem value="NO">NO</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 rounded-lg border p-3 text-sm">
                      <Checkbox checked={line.millCert} onCheckedChange={(checked) => patchLine(line.id, { millCert: checked === true })} />
                      Mill Cert Required
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Received By Contractor & Date</Label>
                        <Input className="mt-1" value={line.receivedByContractorDate} onChange={(event) => patchLine(line.id, { receivedByContractorDate: event.target.value })} />
                      </div>
                      <div>
                        <Label>Return Qty</Label>
                        <Input className="mt-1" type="number" min="0" value={line.returnQty} onChange={(event) => patchLine(line.id, { returnQty: event.target.value })} />
                      </div>
                    </div>
                    <div>
                      <Label>IRT No</Label>
                      <Input className="mt-1" value={line.irtNo} onChange={(event) => patchLine(line.id, { irtNo: event.target.value })} />
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input className="mt-1" type="date" value={line.lineDate} onChange={(event) => patchLine(line.id, { lineDate: event.target.value })} />
                    </div>
                  </>
                ) : null}

                <div>
                  <Label>Remark</Label>
                  <Textarea className="mt-1" rows={2} value={line.remark} onChange={(event) => patchLine(line.id, { remark: event.target.value })} />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" onClick={() => void saveLine(line)} disabled={savingLineId === line.id}>
                    {savingLineId === line.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save Item
                  </Button>
                  <Button type="button" variant="outline" onClick={() => void deleteLine(line)} disabled={savingLineId === line.id}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        </>
      )}
    </div>
  );
}
